import * as React from "react";

import { default as DataGrid } from "../../../components/LocalizedDataGrid";
import { GridToolbarContainer, GridActionsCellItem } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AlertEditPopUp from "./AlertEditPopUp";
import Button from "@mui/material/Button";

import { useTranslation } from "react-i18next";
import { ApiContext } from "../../../App";

const EXACT_VALUE = "EXACT_VALUE";
const ACCEPTED_RANGE = "ACCEPTED_RANGE";

const defaultData = {
	time_ranges: [
		{
			iso_week_day: 1,
			start: "09:00",
			end: "17:00",
		},
		{
			iso_week_day: 2,
			start: "09:00",
			end: "17:00",
		},
		{
			iso_week_day: 3,
			start: "09:00",
			end: "17:00",
		},
		{
			iso_week_day: 4,
			start: "09:00",
			end: "17:00",
		},
		{
			iso_week_day: 5,
			start: "09:00",
			end: "17:00",
		},
		{
			iso_week_day: 6,
			start: "09:00",
			end: "17:00",
		},
		{
			iso_week_day: 7,
			start: "09:00",
			end: "17:00",
		},
	],
	category: ACCEPTED_RANGE,
	name: "",
	message: "",
	priority: "LOW",
	min_value: "15.0",
	max_value: "25.0",
	points: [],
	facility: "",
};

export default function AlertsEdit(props) {
	const {  facility } = props;

	const { t } = useTranslation();

	const [rows, setRows] = React.useState([]);
	const [points, setPoints] = React.useState([]);
	const [controllers, setControllers] = React.useState([]);

	const [editPopUpOpen, setEditPopUpOpen] = React.useState(false);
	const [editPopUpData, setEditPopUpData] = React.useState(defaultData);

	const {alertsApi, pointsApi, controllerApi} = React.useContext(ApiContext)

	React.useEffect(() => {
		alertsApi.list(
			facility,
			(data) => {
				let data_with_id = data.map((dict) => {
					let with_id_url = { ...dict, id: dict.url };
					delete with_id_url.url;
					return with_id_url;
				});

				setRows(data_with_id);
			},
			() => {
				window.alert(t("Couldn't connect to server."));
			}
		);

		pointsApi.list(
			facility,
			(data) => {
				setPoints(data);
			},
			() => {
				window.alert(t("Couldn't connect to server."));
			}
		);

		controllerApi.list(
			facility,
			(data) => {
				setControllers(data);
			},
			() => {
				window.alert(t("Couldn't connect to server."));
			}
		);


	}, [facility]);

	const handleEditClick = (id) => {
		setEditPopUpOpen(true);
		setEditPopUpData(rows.find((item) => item.id === id));
	};

	const handleDuplicateClick = (id) => {
		const from_data = rows.filter((row) => row.id === id)[0];
		let to_data = { ...from_data };
		delete to_data.id;

		setEditPopUpData(to_data);
		setEditPopUpOpen(true);
	};

	const handleDeleteClick = (id) => {
		const confirmed = window.confirm(t("Delete this alert?"));

		if (confirmed) {
			alertsApi.delete(
				id,
				() => {
					setRows(rows.filter((row) => row.id !== id));
				},
				() => {
					alert(t("The alert couldn't be deleted."));
				}
			);
		}
	};

	const handleEditPopUpClosedWithoutSaving = () => {
		setEditPopUpOpen(false);
	};

	const handleEditPopUpClosedWithUpdate = (new_data) => {
		if (rows.find((item) => item.id === new_data.id))
			setRows(rows.map((row) => (row.id === new_data.id ? new_data : row)));
		else setRows((oldRows) => [...oldRows, new_data]);

		setEditPopUpOpen(false);
	};

	const handleAddAlert = () => {
		setEditPopUpData({ ...defaultData, facility: facility });
		setEditPopUpOpen(true);
	};

	const getValue = (data) => {
		if (data.row.category === EXACT_VALUE) return "= " + data.row.exact_value;
		else
			return (
				t("Outside range") +
				" " +
				parseFloat(data.row.min_value).toFixed(1) +
				" - " +
				parseFloat(data.row.max_value).toFixed(1)
			);
	};

	const columns = [
		{
			field: "priority",
			headerName: t("Priority"),
			minWidth: 80,
		},
		{
			field: "name",
			headerName: t("Name"),
			flex: 1,
			minWidth: 80,
		},
		{
			field: "trigger",
			headerName: t("Trigger"),
			flex: 1,
			minWidth: 80,
			valueGetter: (data) => getValue(data),
		},
		{
			field: "actions",
			type: "actions",
			headerName: t("Actions"),
			flex: 0.5,
			minWidth: 150,
			cellClassName: "actions",
			getActions: ({ id }) => {
				return [
					<GridActionsCellItem
						icon={<EditIcon />}
						label={t("Edit")}
						className="textPrimary"
						onClick={() => handleEditClick(id)}
						color="inherit"
					/>,
					<GridActionsCellItem
						icon={<ContentCopyIcon />}
						label={t("Duplicate")}
						onClick={() => handleDuplicateClick(id)}
						color="inherit"
					/>,
					<GridActionsCellItem
						icon={<DeleteIcon />}
						label={t("Delete")}
						onClick={() => handleDeleteClick(id)}
						color="inherit"
					/>,
				];
			},
		},
	];

	const gridToolbar = (props) => {
		return (
			<GridToolbarContainer>
				<Button
					color="primary"
					size="small"
					startIcon={<AddIcon />}
					onClick={() => props.onClick()}
				>
					{t("Add alert")}
				</Button>
			</GridToolbarContainer>
		);
	};

	return (
		<>
			<DataGrid
				rows={rows}
				columns={columns}
				components={{
					Toolbar: gridToolbar,
				}}
				componentsProps={{
					toolbar: { onClick: handleAddAlert },
				}}
			/>
			<AlertEditPopUp
				open={editPopUpOpen}
				data={editPopUpData}
				onCloseWithoutSaving={handleEditPopUpClosedWithoutSaving}
				onCloseWithUpdate={handleEditPopUpClosedWithUpdate}
				points={points}
				controllers={controllers}
			/>
		</>
	);
}
